import { useState, useCallback } from 'react';
import { useETLMonitoring } from '../../hooks/useETLMonitoring';
import { FileDropzone } from '../../components/etl/FileDropzone';
import { DataPreview } from '../../components/etl/DataPreview';
import { EntitySelector } from '../../components/etl/EntitySelector';
import * as XLSX from 'xlsx';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ETLPageProps {}

export function ETLPage() {
  const [entity, setEntity] = useState<string>('');
  const [data, setData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [validation, setValidation] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const { captureError, captureValidationError, logEvent } = useETLMonitoring(entity);

  const handleFileSelect = useCallback((selectedFile: File) => {
    setFile(selectedFile);
    setData([]);
    setHeaders([]);
    setValidation(null);
    setImportResult(null);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = e.target?.result;
        if (!result) {
          setError('Error al leer el archivo');
          return;
        }
        
        const workbook = XLSX.read(result, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        
        if (jsonData.length === 0) {
          setError('El archivo está vacío');
          return;
        }

        const headers = Object.keys(jsonData[0] as object);
        setHeaders(headers);
        setData(jsonData);
        logEvent('file_loaded', { fileName: selectedFile.name, rowCount: jsonData.length });
        
        if (entity) {
          validateData(jsonData, entity);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Error al leer archivo');
        captureError(error, { fileName: selectedFile.name });
        setError(error.message);
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  }, [entity, captureError, logEvent]);

  const validateData = useCallback(async (dataToValidate: any[], entityType: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8787'}/api/etl/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          data: dataToValidate,
          entity: entityType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al validar datos');
      }

      const result = await response.json();
      const validationResult = result.data || {};
      setValidation(validationResult);
      
      if (validationResult.errors && validationResult.errors.length > 0) {
        validationResult.errors.slice(0, 10).forEach((err: any) => {
          captureValidationError(err.row, err.field, err.value, err.message);
        });
      }
      
      logEvent('validation_complete', {
        entity: entityType,
        total: validationResult.total,
        valid: validationResult.validCount,
        errors: validationResult.errors?.length || 0
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error al validar datos');
      captureError(error, { entity: entityType });
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [captureError, captureValidationError, logEvent]);

  const handleEntityChange = useCallback((newEntity: string) => {
    setEntity(newEntity);
    if (data.length > 0) {
      validateData(data, newEntity);
    }
  }, [data, validateData]);

  const handleImport = useCallback(async () => {
    if (!data.length || !entity) return;

    setIsImporting(true);
    setError(null);
    setImportResult(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8787'}/api/etl/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          data: data,
          entity: entity
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al importar datos');
      }

      const result = await response.json();
      const importData = result.data || {};
      setImportResult(importData);
      
      if (importData.success) {
        setFile(null);
        setData([]);
        setHeaders([]);
        setValidation(null);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error al importar datos');
      captureError(error, { entity });
      setError(error.message);
    } finally {
      setIsImporting(false);
    }
  }, [data, entity, captureError]);

  const handleReset = useCallback(() => {
    setFile(null);
    setData([]);
    setHeaders([]);
    setValidation(null);
    setImportResult(null);
    setError(null);
    setEntity('');
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Ingesta de Datos (ETL)</h1>
        <p className="text-gray-600">Importa datos históricos desde archivos Excel a la plataforma</p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-600 rounded-md border border-red-200">
          ❌ {error}
        </div>
      )}

      {importResult && (
        <div className={`p-4 rounded-md border ${
          importResult.success 
            ? 'bg-green-50 border-green-200 text-green-700' 
            : 'bg-yellow-50 border-yellow-200 text-yellow-700'
        }`}>
          <div className="flex items-center gap-3">
            <span className="text-2xl">{importResult.success ? '✅' : '⚠️'}</span>
            <div>
              <p className="font-medium">
                {importResult.success ? 'Importación completada' : 'Importación con advertencias'}
              </p>
              <p className="text-sm">
                {importResult.imported || 0} registros importados, {importResult.failed || 0} fallidos
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Selección de entidad */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-medium mb-4">1. Selecciona el tipo de datos</h3>
        <EntitySelector 
          value={entity} 
          onChange={handleEntityChange}
          disabled={isLoading || isImporting}
        />
      </div>

      {/* Carga de archivo */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-medium mb-4">2. Carga tu archivo Excel</h3>
        {!file ? (
          <FileDropzone 
            onFileSelect={handleFileSelect}
            accept=".xlsx,.xls"
            maxSize={50}
          />
        ) : (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📄</span>
              <div>
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-gray-500">
                  {(file.size / 1024).toFixed(2)} KB • {data.length} filas
                </p>
              </div>
            </div>
            <button
              onClick={handleReset}
              className="text-red-600 hover:text-red-800 text-sm font-medium"
            >
              Cambiar archivo
            </button>
          </div>
        )}
      </div>

      {/* Previsualización de datos */}
      {data.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">3. Previsualización de datos</h3>
            {validation && (
              <span className={`text-sm font-medium ${
                validation.valid ? 'text-green-600' : 'text-red-600'
              }`}>
                {validation.valid ? '✅ Datos válidos' : `⚠️ ${validation.errors?.length || 0} errores`}
              </span>
            )}
          </div>

          <DataPreview 
            data={data} 
            headers={headers}
            validation={validation}
            isLoading={isLoading}
          />

          {validation && !validation.valid && validation.errors?.length > 0 && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm font-medium text-yellow-800">Errores de validación:</p>
              <ul className="mt-2 space-y-1">
                {validation.errors.slice(0, 5).map((err: any, i: number) => (
                  <li key={i} className="text-sm text-yellow-700">
                    Fila {err.row}: {err.field} - {err.message}
                  </li>
                ))}
                {validation.errors.length > 5 && (
                  <li className="text-sm text-yellow-700">
                    ... y {validation.errors.length - 5} errores más
                  </li>
                )}
              </ul>
            </div>
          )}

          {validation && validation.valid && (
            <div className="mt-4">
              <button
                onClick={handleImport}
                disabled={isImporting}
                className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
              >
                {isImporting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importando...
                  </span>
                ) : (
                  '📥 Importar Datos'
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}